import React, { useState, useEffect } from 'react';
import { App, Card, Button, List, Upload, Space, Typography, Empty, Spin, Popconfirm, Modal, Input, Divider, Tag, Select, Alert, Pagination, Drawer, Radio } from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  EditOutlined,
  SaveOutlined,
  BranchesOutlined,
  InfoCircleOutlined,
  ApartmentOutlined,
  SearchOutlined,
  SendOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { kbApiClient as axios, KB_API_BASE } from '../api';

const { TextArea } = Input;

const KnowledgeStudio = () => {
  const { message } = App.useApp();
  const [files, setFiles] = useState([]);
  const [cogneeEnabled, setCogneeEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'edit'
  const [chunks, setChunks] = useState([]);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [chunkPage, setChunkPage] = useState(1);
  const [chunkTotal, setChunkTotal] = useState(0);
  const CHUNK_PAGE_SIZE = 50;
  const [editingChunk, setEditingChunk] = useState(null);
  const [docDescription, setDocDescription] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [retrying, setRetrying] = useState({});

  // ── Graph visualisation modal ──
  const [vizFile, setVizFile] = useState(null);
  const [vizLoading, setVizLoading] = useState(false);
  const [vizHtml, setVizHtml] = useState('');

  // ── Test search drawer ──
  const [searchFile, setSearchFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('GRAPH_COMPLETION');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = not yet searched

  const loadFiles = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${KB_API_BASE}/library`);
      if (res.data.success) {
        setFiles(res.data.files || []);
        if (res.data.cogneeEnabled !== undefined) setCogneeEnabled(res.data.cogneeEnabled);
      }
    } catch (err) {
      message.error('Failed to load library files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFiles(); }, []);

  // ── Check status for a single file (manual + auto) ──
  const [checkingStatus, setCheckingStatus] = useState({});
  const checkFileStatus = async (file, silent = false) => {
    if (checkingStatus[file._id]) return;
    setCheckingStatus(prev => ({ ...prev, [file._id]: true }));
    try {
      const res = await axios.post(`${KB_API_BASE}/files/${file._id}/check-status`);
      if (res.data.status !== file.cogneeStatus) {
        // Status changed — reload the full list
        await loadFiles();
        if (res.data.status === 'ready' && !silent)
          message.success(`✅ "${file.originalName}" — knowledge graph is ready!`);
        else if (res.data.status === 'failed' && !silent)
          message.error(`❌ "${file.originalName}" — processing failed. Try re-uploading.`);
      } else if (!silent) {
        message.info(`Still processing… check back in a minute.`);
      }
    } catch (err) {
      if (!silent) message.error('Status check failed: ' + err.message);
    } finally {
      setCheckingStatus(prev => ({ ...prev, [file._id]: false }));
    }
  };

  // No auto-polling — user clicks "Check Status" to get latest state.

  const loadChunks = async (fileId, page = 1) => {
    setChunksLoading(true);
    try {
      const res = await axios.get(`${KB_API_BASE}/files/${fileId}/chunks`, {
        params: { page, limit: CHUNK_PAGE_SIZE }
      });
      if (res.data.success) {
        setChunks(res.data.chunks || []);
        setChunkTotal(res.data.pagination?.total || 0);
        setChunkPage(page);
      }
    } catch (err) {
      message.error('Failed to load document structure');
    } finally {
      setChunksLoading(false);
    }
  };

  const openStudio = (file) => {
    setSelectedFile(file);
    setViewMode('edit');
    loadChunks(file._id);
  };

  const openViz = (file) => {
    // Just set the file — the iframe will load the URL directly from the backend.
    // This avoids the about:srcdoc base-URL problem where D3 / CDN scripts fail.
    setVizFile(file);
    setVizHtml('');   // kept for "Open in new tab" fallback
    setVizLoading(false);
  };

  const vizUrl = vizFile
    ? `${KB_API_BASE}/visualize/${encodeURIComponent(vizFile.templateId)}`
    : '';

  const runTestSearch = async () => {
    if (!searchQuery.trim() || !searchFile) return;
    setSearchLoading(true);
    setSearchResults(null);
    try {
      const res = await axios.post(`${KB_API_BASE}/test-search`, {
        templateId: searchFile.templateId,
        datasetName: searchFile.cogneeDatasetName || null,  // actual Cognee dataset name
        query: searchQuery.trim(),
        searchType,
      });
      if (res.data.success) setSearchResults(res.data);
      else message.error(res.data.message);
    } catch (err) {
      message.error('Search failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUpdateChunk = async (chunkId, values) => {
    try {
      const res = await axios.patch(`${KB_API_BASE}/chunks/${chunkId}`, values);
      if (res.data.success) {
        message.success('Chunk updated & re-indexed');
        setChunks(prev => prev.map(c => c._id === chunkId ? res.data.chunk : c));
        setEditingChunk(null);
      }
    } catch (err) {
      message.error('Failed to update chunk');
    }
  };

  const handleRetry = async (file) => {
    if (retrying[file._id]) return;
    setRetrying(prev => ({ ...prev, [file._id]: true }));
    try {
      const res = await axios.post(`${KB_API_BASE}/files/${file._id}/retry`, {
        docDescription: file.docDescription || "Knowledge base document"
      });
      if (res.data.success) {
        message.success(res.data.message || 'Processing re-triggered.');
        await loadFiles();
      }
    } catch (err) {
      message.error('Retry failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setRetrying(prev => ({ ...prev, [file._id]: false }));
    }
  };

  const handleDeleteFile = async (file) => {
    try {
      const res = await axios.delete(`${KB_API_BASE}/templates/${file.templateId}/knowledge-base/${file._id}`);
      if (res.data.success) {
        message.success('File removed from library');
        loadFiles();
      }
    } catch (err) {
      message.error('Failed to delete file');
    }
  };

  // ── Chunk editor view ──────────────────────────────────────────────────────
  if (viewMode === 'edit') {
    return (
      <div className="studio-editor-container">
        <Card
          title={
            <Space>
              <Button onClick={() => setViewMode('list')} size="small">Back</Button>
              <FileTextOutlined />
              <Typography.Text strong>{selectedFile?.originalName}</Typography.Text>
              <Tag color="blue">{chunkTotal} Chunks</Tag>
            </Space>
          }
          className="studio-card"
        >
          <Alert
            message="Document Structure Guide"
            description={
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                <li><b>Hierarchy Tags:</b> Use multiple tags to define hierarchy (e.g., "HOTEL", "ROOMS", "DELUXE").</li>
                <li><b>Context Prepending:</b> All tags are joined to create an automatic [CONTEXT: ...] prefix for the AI.</li>
                <li><b>Specificity:</b> Smaller chunks with highly specific tags result in the most accurate RAG retrieval.</li>
              </ul>
            }
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 20 }}
          />

          {chunksLoading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin size="large">
                <div style={{ padding: 20 }}>Analyzing structure…</div>
              </Spin>
            </div>
          ) : (
            <List
              dataSource={chunks}
              itemLayout="vertical"
              footer={
                chunkTotal > CHUNK_PAGE_SIZE && (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Pagination
                      current={chunkPage}
                      pageSize={CHUNK_PAGE_SIZE}
                      total={chunkTotal}
                      showTotal={(t) => `${t} chunks total`}
                      onChange={(page) => loadChunks(selectedFile._id, page)}
                      showSizeChanger={false}
                    />
                  </div>
                )
              }
              renderItem={(chunk, index) => (
                <div
                  key={chunk._id}
                  className={`studio-chunk-item ${editingChunk?._id === chunk._id ? 'editing' : ''}`}
                >
                  {editingChunk?._id === chunk._id ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text type="secondary">Primary & Secondary Tags (Hierarchy)</Typography.Text>
                      <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder="Add tags (e.g. HOTEL, ROOMS)"
                        value={editingChunk.tags}
                        onChange={val => setEditingChunk({ ...editingChunk, tags: val.map(t => t.toUpperCase()) })}
                        tokenSeparators={[',']}
                      />
                      <Typography.Text type="secondary">Chunk Content</Typography.Text>
                      <TextArea
                        rows={6}
                        value={editingChunk.text}
                        onChange={e => setEditingChunk({ ...editingChunk, text: e.target.value })}
                      />
                      <Space>
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          onClick={() => handleUpdateChunk(chunk._id, { text: editingChunk.text, tags: editingChunk.tags })}
                        >
                          Sync Changes
                        </Button>
                        <Button onClick={() => setEditingChunk(null)}>Cancel</Button>
                      </Space>
                    </Space>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Space wrap>
                          <Tag color="purple">#{index + 1}</Tag>
                          {(chunk.tags || ['GENERAL']).map(tag => (
                            <Tag key={tag} color="geekblue">{tag}</Tag>
                          ))}
                        </Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => setEditingChunk({ ...chunk })}
                          size="small"
                        >
                          Edit Structure
                        </Button>
                      </div>
                      <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{chunk.text}</Typography.Text>
                    </>
                  )}
                </div>
              )}
            />
          )}
        </Card>
      </div>
    );
  }

  // ── Library list view ──────────────────────────────────────────────────────
  return (
    <div className="knowledge-studio-container">
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Typography.Title level={2}>Knowledge Studio</Typography.Title>
          <Typography.Paragraph type="secondary">
            Review, restructure, and visualise your uploaded documents to ensure maximum accuracy for your agents.
          </Typography.Paragraph>
        </div>
        <Space>
          <Button icon={<LoadingOutlined spin={loading} />} onClick={loadFiles}>
            Refresh Library
          </Button>
          <Button type="primary" icon={<UploadOutlined />} size="large" onClick={() => setUploadModalOpen(true)}>
            Upload to Library
          </Button>
        </Space>
      </div>

      {/* ── Upload Modal ── */}
      <Modal
        title={<Space><UploadOutlined /> Upload to Knowledge Library</Space>}
        open={uploadModalOpen}
        onCancel={() => { setUploadModalOpen(false); setPendingFile(null); setDocDescription(''); }}
        footer={null}
        width={520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="Knowledge Graph Ingestion"
            description="Cognee will automatically extract entities, relationships, and build a knowledge graph from your document — enabling accurate, reasoning-based retrieval."
            type="info"
            showIcon
          />

          <div>
            <Typography.Text strong>Document Description *</Typography.Text>
            <Input
              placeholder='e.g. "The Leela Palace New Delhi — dining, rooms, spa, policies and hotel overview"'
              value={docDescription}
              onChange={e => setDocDescription(e.target.value)}
              style={{ marginTop: 8 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              This guides Cognee's entity extraction. More specific = better graph.
            </Typography.Text>
          </div>

          <div>
            <Typography.Text strong>Select File</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Upload
                accept=".txt,.pdf,.md,.csv,.json,.docx"
                showUploadList={!!pendingFile}
                maxCount={1}
                beforeUpload={(file) => { setPendingFile(file); return false; }}
                onRemove={() => setPendingFile(null)}
                fileList={pendingFile ? [pendingFile] : []}
              >
                <Button icon={<UploadOutlined />}>Choose File</Button>
              </Upload>
            </div>
          </div>

          <Button
            type="primary"
            block
            size="large"
            loading={loading}
            disabled={!pendingFile || !docDescription.trim()}
            onClick={async () => {
              if (!pendingFile || !docDescription.trim()) return;
              setLoading(true);
              try {
                const formData = new FormData();
                formData.append('file', pendingFile);
                formData.append('docDescription', docDescription.trim());
                const res = await axios.post(`${KB_API_BASE}/upload`, formData);
                if (res.data.success) {
                  const est = res.data.estMinutes || '2–5';
                  message.success({
                    content: (
                      <div>
                        <b>✅ Document Uploaded!</b>
                        <br />
                        <span style={{ fontSize: 12 }}>
                          Local RAG is <b>Ready</b>. Advanced RAG (Cognee) is now building deeper insights (est. {est} min). 
                          Please wait for the "Advanced" status to turn green for better accuracy.
                        </span>
                      </div>
                    ),
                    duration: 10,
                  });
                  setUploadModalOpen(false);
                  setPendingFile(null);
                  setDocDescription('');
                  loadFiles();
                }
              } catch (err) {
                message.error('Upload failed: ' + (err.response?.data?.message || err.message));
              } finally {
                setLoading(false);
              }
            }}
          >
            {loading ? 'Uploading to Cognee…' : 'Upload & Build Graph'}
          </Button>
        </Space>
      </Modal>

      {/* ── Graph Visualisation Modal ── */}
      <Modal
        title={
          <Space>
            <ApartmentOutlined style={{ color: '#722ed1' }} />
            <span>Knowledge Graph — {vizFile?.originalName}</span>
          </Space>
        }
        open={!!vizFile}
        onCancel={() => { setVizFile(null); setVizHtml(''); setVizLoading(false); }}
        footer={
          <Button
            onClick={() => vizUrl && window.open(vizUrl, '_blank')}
            disabled={!vizUrl}
          >
            Open in new tab ↗
          </Button>
        }
        width="94vw"
        style={{ top: 10 }}
        styles={{ body: { padding: 0, height: '85vh', overflow: 'hidden' } }}
        destroyOnHidden
      >
        {/* iframe src → kb-server proxies to Cognee; browser gets proper origin so D3/CDN scripts run */}
        {vizFile && (
          <iframe
            key={vizFile._id}
            title="Knowledge Graph"
            src={vizUrl}
            style={{ width: '100%', height: '85vh', border: 'none', display: 'block' }}
          />
        )}
      </Modal>

      {/* ── Test Search Drawer ── */}
      <Drawer
        title={
          <Space>
            <SearchOutlined style={{ color: '#1890ff' }} />
            <span>Test Knowledge Base — {searchFile?.originalName}</span>
          </Space>
        }
        open={!!searchFile}
        onClose={() => { setSearchFile(null); setSearchResults(null); setSearchQuery(''); }}
        width={620}
        extra={
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={searchLoading}
            disabled={!searchQuery.trim()}
            onClick={runTestSearch}
          >
            Search
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Search Type</Typography.Text>
            <div style={{ marginTop: 6 }}>
              <Radio.Group value={searchType} onChange={e => setSearchType(e.target.value)} buttonStyle="solid">
                <Radio.Button value="GRAPH_COMPLETION">Graph Completion</Radio.Button>
                <Radio.Button value="SUMMARIES">Summaries</Radio.Button>
                <Radio.Button value="CHUNKS">Chunks</Radio.Button>
              </Radio.Group>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
              Graph Completion = full reasoning answer &nbsp;|&nbsp; Summaries = topic summaries &nbsp;|&nbsp; Chunks = raw text fragments
            </Typography.Text>
          </div>

          <Input.Search
            placeholder='e.g. "What is the check-in time at The Leela Mumbai?"'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onSearch={runTestSearch}
            enterButton="Search"
            loading={searchLoading}
            size="large"
          />

          {searchLoading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large">
                <div style={{ padding: 20 }}>Querying knowledge graph…</div>
              </Spin>
            </div>
          )}

          {searchResults && !searchLoading && (
            <div>
              <Divider orientation="left">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {searchResults.results.length} result(s) for "{searchResults.query}"
                </Typography.Text>
              </Divider>

              {searchResults.results.length === 0 ? (
                <Alert
                  type="warning"
                  message="No results found"
                  description="Try a different query or check that the graph has finished processing (usually takes 30–90s after upload)."
                />
              ) : (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {searchResults.source === 'local' && (
                    <Alert 
                      type="info" 
                      message="Showing Local RAG Results" 
                      description="Advanced Graph RAG is still processing. Falling back to local vector search for immediate answers."
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  )}
                  {searchResults.results.map((text, i) => (
                    <Card
                      key={i}
                      size="small"
                      style={{ background: '#fafafa', borderLeft: searchResults.source === 'local' ? '3px solid #faad14' : '3px solid #1890ff' }}
                      styles={{ body: { padding: '12px 16px' } }}
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography.Text type="secondary" style={{ fontSize: 10 }}>Chunk #{i+1}</Typography.Text>
                          <Tag color={searchResults.source === 'local' ? 'orange' : 'blue'} style={{ fontSize: 9, margin: 0 }}>
                            {searchResults.source === 'local' ? 'Local Vector' : 'Cognee Graph'}
                          </Tag>
                        </div>
                      }
                    >
                      <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                        {text}
                      </Typography.Text>
                    </Card>
                  ))}
                </Space>
              )}
            </div>
          )}
        </Space>
      </Drawer>

      {/* ── File list ── */}
      <Card className="studio-list-card">
        <List
          loading={loading}
          dataSource={files}
          renderItem={(file) => (
            <List.Item
              className="studio-list-item"
              actions={[
                <Button
                  key="refresh"
                  icon={<LoadingOutlined spin={checkingStatus[file._id]} />}
                  onClick={() => checkFileStatus(file)}
                  title="Refresh processing status"
                >
                  Status
                </Button>,
                <Button
                  key="retry"
                  icon={<SyncOutlined spin={retrying[file._id]} />}
                  onClick={() => handleRetry(file)}
                  disabled={retrying[file._id]}
                  title="Retry AI Tagging & Cognee Graph"
                >
                  Retry
                </Button>,
                file.cogneeStatus === 'processing' ? (
                  <Tag key="status" icon={<LoadingOutlined spin />} color="processing" style={{ margin: 0, padding: '4px 8px' }}>
                    Building Graph
                  </Tag>
                ) : (
                  <Button
                    key="viz"
                    type="primary"
                    ghost
                    icon={<ApartmentOutlined />}
                    onClick={() => openViz(file)}
                    disabled={file.cogneeStatus === 'failed' || file.cogneeStatus === 'unknown'}
                    style={file.cogneeStatus === 'ready' ? { borderColor: '#722ed1', color: '#722ed1' } : {}}
                  >
                    View Graph
                  </Button>
                ),
                <Button
                  key="search"
                  type="primary"
                  ghost
                  icon={<SearchOutlined />}
                  onClick={() => { setSearchFile(file); setSearchResults(null); setSearchQuery(''); }}
                  disabled={file.cogneeStatus === 'processing' || file.cogneeStatus === 'failed'}
                  style={file.cogneeStatus === 'ready' ? { borderColor: '#13c2c2', color: '#13c2c2' } : {}}
                >
                  Test Search
                </Button>,
                /* Review Chunks only for non-Cognee (local-vector) files */
                ...(!cogneeEnabled ? [
                  <Button
                    key="chunks"
                    type="primary"
                    ghost
                    icon={<BranchesOutlined />}
                    onClick={() => openStudio(file)}
                  >
                    Review Chunks
                  </Button>,
                ] : []),
                <Popconfirm key="delete" title="Remove from library?" onConfirm={() => handleDeleteFile(file)}>
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                title={<Typography.Text strong>{file.originalName}</Typography.Text>}
                description={
                  <Space split={<Divider type="vertical" />} wrap>
                    <Tag color="success" icon={<CheckCircleOutlined />}>Local RAG: Ready</Tag>
                    {file.cogneeStatus === 'ready' && <Tag color="purple" icon={<CheckCircleOutlined />}>Advanced RAG: Ready</Tag>}
                    {file.cogneeStatus === 'processing' && <Tag color="processing" icon={<LoadingOutlined spin />}>Advanced RAG: Processing...</Tag>}
                    {file.cogneeStatus === 'failed' && <Tag color="error" icon={<ClockCircleOutlined />}>Advanced RAG: Failed</Tag>}
                    <Tag>{file.templateId}</Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(file.uploadedAt).toLocaleDateString()}
                    </Typography.Text>
                  </Space>
                }
              />
            </List.Item>
          )}
          locale={{ emptyText: <Empty description="Knowledge Library is empty." /> }}
        />
      </Card>

      <style>{`
        .knowledge-studio-container { padding: 24px; max-width: 1200px; margin: 0 auto; }
        .studio-list-card { border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
        .studio-list-item { padding: 20px 24px !important; border-bottom: 1px solid #f0f0f0; }
        .studio-list-item:last-child { border-bottom: none; }
        .ant-list-item-action { margin-left: 48px !important; }
        .ant-list-item-action li { padding: 0 8px !important; }
        .ant-btn-ghost:disabled { color: rgba(0, 0, 0, 0.25) !important; border-color: #d9d9d9 !important; }
      `}</style>
    </div>
  );
};

export default KnowledgeStudio;
